import React, { useState, useEffect } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { FiImage } from 'react-icons/fi'
import LoadingSpinner from './LoadingSpinner'

interface GigImageHeroProps {
    images?: string[]
    alt?: string
    className?: string
    showIndicators?: boolean
    showNavigation?: boolean
    autoSlide?: boolean
    slideInterval?: number
    aspectRatio?: 'video' | 'banner' | 'square'
    overlay?: boolean
    children?: React.ReactNode
}



const GigImageHero: React.FC<GigImageHeroProps> = ({
    images = [],
    alt = 'Gig image',
    className = '',
    showIndicators = true,
    showNavigation = true,
    autoSlide = true,
    slideInterval = 5000,
    aspectRatio = 'banner',
    overlay = true,
    children
}) => {
    const [currentIndex, setCurrentIndex] = useState(0)
    const [imageUrls, setImageUrls] = useState<(string | null)[]>([])
    const [loadingStates, setLoadingStates] = useState<boolean[]>([])
    const [errorStates, setErrorStates] = useState<boolean[]>([])

    // Load all images
    useEffect(() => {
        if (!images.length) return

        const fetchAllImages = async () => {
            const urls: (string | null)[] = []
            const loading: boolean[] = []
            const errors: boolean[] = []

            for (let i = 0; i < images.length; i++) {
                loading[i] = true
                errors[i] = false
            }
            setLoadingStates([...loading])
            setErrorStates([...errors])

            for (let i = 0; i < images.length; i++) {
                try {
                    const apiBase = (import.meta.env.VITE_API_URL || 'http://localhost:5000').replace(/\/api$/, '')
                    const response = await fetch(`${apiBase}/api/v1/upload/${images[i]}`)
                    if (!response.ok) throw new Error('Failed to load image')

                    const data = await response.json()
                    if (data.success && data.data?.image?.base64Data) {
                        const { base64Data, mimeType } = data.data.image
                        const byteCharacters = atob(base64Data)
                        const byteNumbers = new Array(byteCharacters.length)
                        for (let j = 0; j < byteCharacters.length; j++) {
                            byteNumbers[j] = byteCharacters.charCodeAt(j)
                        }
                        const byteArray = new Uint8Array(byteNumbers)
                        const blob = new Blob([byteArray], { type: mimeType })
                        const url = URL.createObjectURL(blob)
                        urls[i] = url
                    } else {
                        urls[i] = null
                        errors[i] = true
                    }
                } catch (err) {
                    urls[i] = null
                    errors[i] = true
                }
                loading[i] = false
                setLoadingStates([...loading])
                setErrorStates([...errors])
            }

            setImageUrls([...urls])
        }

        fetchAllImages()

        // Cleanup
        return () => {
            imageUrls.forEach(url => {
                if (url) URL.revokeObjectURL(url)
            })
        }
    }, [images])

    // Auto-slide functionality
    useEffect(() => {
        if (!autoSlide || images.length <= 1) return

        const interval = setInterval(() => {
            setCurrentIndex(prev => (prev + 1) % images.length)
        }, slideInterval)

        return () => clearInterval(interval)
    }, [autoSlide, images.length, slideInterval])

    const aspectRatioClasses = {
        video: 'aspect-video', // 16:9
        banner: 'aspect-[21/9]', // Wide banner
        square: 'aspect-square' // 1:1
    }

    const nextSlide = () => {
        setCurrentIndex(prev => (prev + 1) % images.length)
    }

    const prevSlide = () => {
        setCurrentIndex(prev => (prev - 1 + images.length) % images.length)
    }

    const goToSlide = (index: number) => {
        setCurrentIndex(index)
    }

    if (!images.length) {
        return null
    }

    const currentImage = imageUrls[currentIndex]
    const isLoading = loadingStates[currentIndex]
    const hasError = errorStates[currentIndex]

    return (
        <div className={`relative w-full ${aspectRatioClasses[aspectRatio]} bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden group ${className}`}>
            {/* Main Image */}
            {isLoading ? (
                <div className="absolute inset-0 flex items-center justify-center">
                    <LoadingSpinner size="lg" />
                </div>
            ) : currentImage && !hasError ? (
                <img
                    src={currentImage}
                    alt={`${alt} ${currentIndex + 1}`}
                    className="w-full h-full object-cover"
                />
            ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                    <FiImage className="w-16 h-16 text-gray-400 dark:text-gray-500" />
                </div>
            )}

            {/* Overlay */}
            {overlay && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
            )}

            {/* Navigation Arrows */}
            {showNavigation && images.length > 1 && (
                <>
                    <button
                        onClick={prevSlide}
                        className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        aria-label="Previous image"
                    >
                        <ChevronLeftIcon className="w-4 h-4" />
                    </button>
                    <button
                        onClick={nextSlide}
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        aria-label="Next image"
                    >
                        <ChevronRightIcon className="w-4 h-4" />
                    </button>
                </>
            )}

            {/* Indicators */}
            {showIndicators && images.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {images.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => goToSlide(index)}
                            className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentIndex
                                ? 'bg-white shadow-lg'
                                : 'bg-white/50 hover:bg-white/80'
                                }`}
                            aria-label={`Go to image ${index + 1}`}
                        />
                    ))}
                </div>
            )}

            {/* Content Overlay */}
            {children && (
                <div className="absolute inset-0 flex items-end p-4 z-10">
                    {children}
                </div>
            )}
        </div>
    )
}

export default GigImageHero